// src/components/dashboard/HoursBalanceOutliersWidget.js

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDashboard } from '../../contexts/DashboardContext';
import '../../styles/dashboard-widgets.css';

// Local formatting/color helpers.
// Intentionally NOT shared with HoursAccountCard.js's getBalanceColor()
// (that helper isn't exported) — same duplication decision already made
// for the ytdBalance column in StaffManagementPage.js. Exporting/sharing
// a single helper is flagged as an optional follow-up in both places.
const formatBalance = (balance) => {
  if (balance === null || balance === undefined) return '—';
  const sign = balance > 0 ? '+' : '';
  return `${sign}${balance.toFixed(1)}h`;
};

const getBalanceColorClass = (balance) => {
  if (balance === null || balance === undefined) return 'balance-unknown';
  if (balance > 0) return 'balance-positive';
  if (balance < 0) return 'balance-negative';
  return 'balance-neutral';
};

const HoursBalanceOutliersWidget = () => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();

  const {
    hoursBalanceOutliers,
    hoursBalanceOutliersLoading,
    hoursBalanceOutliersError,
    loadHoursBalanceOutliers
  } = useDashboard();

  // Fetch on mount. loadHoursBalanceOutliers is a useCallback with
  // [companyId] as its only dependency in DashboardContext, so this
  // effect also naturally re-fires if companyId becomes available
  // after this widget has already mounted (mirrors VacationWidget's
  // companyId-driven re-fetch, without needing a second dependency here).
  useEffect(() => {
    loadHoursBalanceOutliers();
  }, [loadHoursBalanceOutliers]);

  const handleViewAll = () => {
    navigate('/admin/staff');
  };

  const topPositive = hoursBalanceOutliers?.topPositive || [];
  const topNegative = hoursBalanceOutliers?.topNegative || [];
  const staffWithoutAccount = hoursBalanceOutliers?.staffWithoutAccount || 0;
  const hasAnyData = topPositive.length > 0 || topNegative.length > 0;

  // Loading state only shows before the first successful fetch completes
  // (hoursBalanceOutliers is still null) — once data exists, a manual
  // refresh won't blank the widget.
  if (hoursBalanceOutliersLoading && !hoursBalanceOutliers) {
    return (
      <div className="hours-balance-outliers-widget">
        <div className="widget-header">
          <h3>{t('hoursBalanceOutliers.title')}</h3>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('hoursBalanceOutliers.loading')}</p>
        </div>
      </div>
    );
  }

  if (hoursBalanceOutliersError) {
    return (
      <div className="hours-balance-outliers-widget">
        <div className="widget-header">
          <h3>{t('hoursBalanceOutliers.title')}</h3>
        </div>
        <div className="error-state">
          <p className="error-message">× {hoursBalanceOutliersError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hours-balance-outliers-widget">
      <div className="widget-header">
        <h3>{t('hoursBalanceOutliers.title')}</h3>
        {staffWithoutAccount > 0 && (
          <div className="stats-summary">
            <span className="stat">
              <strong>{staffWithoutAccount}</strong>{' '}
              {t('hoursBalanceOutliers.staffWithoutAccount')}
            </span>
          </div>
        )}
      </div>

      {!hasAnyData ? (
        <div className="empty-state">
          <div className="empty-icon">◎</div>
          <h4>{t('hoursBalanceOutliers.empty.title')}</h4>
          <p>{t('hoursBalanceOutliers.empty.description')}</p>
        </div>
      ) : (
        <div className="hours-balance-columns">
          <div className="hours-balance-column">
            <h4 className="hours-balance-column-title">
              {t('hoursBalanceOutliers.topPositive')}
            </h4>
            {topPositive.length === 0 ? (
              <p className="hours-balance-empty-column">
                {t('hoursBalanceOutliers.noPositive')}
              </p>
            ) : (
              <ul className="hours-balance-list">
                {topPositive.map((entry) => (
                  <li key={entry.staffId} className="hours-balance-row">
                    <div className="hours-balance-info">
                      <span className="hours-balance-staff-name">{entry.staffName}</span>
                      <span className="hours-balance-department-name">{entry.departmentName}</span>
                    </div>
                    <span className={`hours-balance-value ${getBalanceColorClass(entry.ytdBalance)}`}>
                      {formatBalance(entry.ytdBalance)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="hours-balance-column">
            <h4 className="hours-balance-column-title">
              {t('hoursBalanceOutliers.topNegative')}
            </h4>
            {topNegative.length === 0 ? (
              <p className="hours-balance-empty-column">
                {t('hoursBalanceOutliers.noNegative')}
              </p>
            ) : (
              <ul className="hours-balance-list">
                {topNegative.map((entry) => (
                  <li key={entry.staffId} className="hours-balance-row">
                    <div className="hours-balance-info">
                      <span className="hours-balance-staff-name">{entry.staffName}</span>
                      <span className="hours-balance-department-name">{entry.departmentName}</span>
                    </div>
                    <span className={`hours-balance-value ${getBalanceColorClass(entry.ytdBalance)}`}>
                      {formatBalance(entry.ytdBalance)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="widget-footer">
        <button onClick={handleViewAll} className="view-all-btn">
          {t('hoursBalanceOutliers.viewAllInStaffList')} →
        </button>
      </div>
    </div>
  );
};

export default HoursBalanceOutliersWidget;